/**
 * Client API centralisé pour communiquer avec le backend Django.
 * Attache automatiquement l'access token, et tente un refresh silencieux
 * en cas de 401 sur un endpoint protégé avant de faire échouer la requête.
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000/api/v1";

const ACCESS_TOKEN_KEY = "lacour_access_token";
const REFRESH_TOKEN_KEY = "lacour_refresh_token";

// Endpoints d'auth pour lesquels une 401 est une vraie erreur (mauvais
// identifiants, token invalide) : inutile - et faux - d'y tenter un refresh.
const NO_REFRESH_PATHS = ["/auth/login/", "/auth/register/", "/auth/refresh/"];

export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(ACCESS_TOKEN_KEY);
}

function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(access: string, refresh: string): void {
  window.localStorage.setItem(ACCESS_TOKEN_KEY, access);
  window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
}

export function clearTokens(): void {
  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  /** Erreurs par champ telles que DRF les renvoie ({"champ": ["message", ...]})
   * — undefined si le corps n'avait pas cette forme (ex. {"detail": "..."}).
   * Permet à un appelant de distinguer PRÉCISÉMENT quel champ a échoué (ex.
   * reset-password : "token" invalide vs "new_password" trop court) plutôt
   * que de se rabattre sur un message générique par code HTTP — voir
   * fix(auth): message réel de reset-password. */
  fields?: Record<string, string[]>;

  constructor(status: number, message: string, fields?: Record<string, string[]>) {
    super(message);
    this.status = status;
    this.fields = fields;
  }
}

// Un seul refresh réseau à la fois : le backend fait tourner le refresh token à
// chaque appel (ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION), donc il est à
// usage unique. Si plusieurs requêtes échouent en 401 en même temps (ex. la page
// d'accueil qui charge plusieurs séries en parallèle), chacune appelant son propre
// refresh en lisant le même token depuis localStorage, la 2e utiliserait un token
// déjà consommé par la 1ère et échouerait avec "Le type de jeton fourni n'est pas
// valide" — constaté en conditions réelles. Toutes les requêtes concurrentes
// attendent donc le même refresh en vol plutôt que d'en déclencher un chacune.
let refreshEnCours: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshEnCours) return refreshEnCours;

  refreshEnCours = (async () => {
    try {
      const refresh = getRefreshToken();
      if (!refresh) return null;

      const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!response.ok) return null;

      // Sans sauvegarder le nouveau refresh token ici, le rafraîchissement suivant
      // échouait systématiquement (token à usage unique, voir plus haut) — constaté
      // en conditions réelles après ~30-60 min de session (2e cycle de refresh).
      const data = (await response.json()) as { access: string; refresh?: string };
      window.localStorage.setItem(ACCESS_TOKEN_KEY, data.access);
      if (data.refresh) {
        window.localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh);
      }
      return data.access;
    } finally {
      refreshEnCours = null;
    }
  })();

  return refreshEnCours;
}

function buildHeaders(options: RequestInit, token: string | null): Headers {
  const headers = new Headers(options.headers);
  // FormData (upload d'avatar) : on laisse le navigateur fixer le
  // Content-Type multipart avec sa boundary, on ne doit pas l'écraser.
  if (!(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}

// Un 204, ou plus généralement un corps vide (certaines réponses DELETE/POST
// n'ont pas de Content-Length), n'a rien à parser : response.json() plante
// sinon avec "Unexpected end of JSON input".
async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

// Le backend renvoie soit { detail: "message explicite" } (ex. quota épuisé),
// soit la forme standard DRF par champ { "champ": ["message", ...] } (erreurs
// de validation de serializer, ex. { "new_password": ["Ce mot de passe est
// trop court..."] }) — sans lire ce 2e cas, un appelant qui a besoin de savoir
// PRÉCISÉMENT quel champ a échoué (voir ApiError.fields) retombait sur un
// message générique par code HTTP, même quand le serveur avait déjà produit
// un texte clair et actionnable pour LE bon champ.
async function erreurApi(response: Response, path: string): Promise<ApiError> {
  try {
    const corps = await response.clone().json();
    if (typeof corps?.detail === "string" && corps.detail) {
      return new ApiError(response.status, corps.detail);
    }
    // Forme par champ : toutes les valeurs sont des tableaux de chaînes.
    if (corps && typeof corps === "object" && Object.values(corps).every((v) => Array.isArray(v))) {
      const fields = corps as Record<string, string[]>;
      const premier = Object.values(fields)[0]?.[0];
      return new ApiError(response.status, premier ?? `Échec de la requête ${path} (${response.status})`, fields);
    }
  } catch {
    // Corps non-JSON ou vide : pas de détail exploitable.
  }
  return new ApiError(response.status, `Échec de la requête ${path} (${response.status})`);
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: buildHeaders(options, token),
  });

  if (response.status === 401 && token && !NO_REFRESH_PATHS.includes(path)) {
    const newAccess = await refreshAccessToken();
    if (newAccess) {
      const retryResponse = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers: buildHeaders(options, newAccess),
      });
      if (!retryResponse.ok) {
        throw await erreurApi(retryResponse, path);
      }
      return parseBody<T>(retryResponse);
    }
    clearTokens();
  }

  if (!response.ok) {
    throw await erreurApi(response, path);
  }

  return parseBody<T>(response);
}

export interface HealthCheckResponse {
  status: string;
  database: string;
}

export function checkHealth(): Promise<HealthCheckResponse> {
  return apiFetch<HealthCheckResponse>("/health/");
}

export interface Profile {
  id: string;
  email: string;
  display_name: string;
  handle: string;
  avatar: string | null;
  bio: string;
  is_lead: boolean;
  is_follower: boolean;
  visibility: "public" | "sur_invitation";
  onboarding_completed: boolean;
  email_notifications_enabled: boolean;
  is_superuser: boolean;
  created_at: string;
  updated_at: string;
}

interface TokenPair {
  access: string;
  refresh: string;
}

export async function register(email: string, password: string, invite?: string): Promise<TokenPair> {
  const tokens = await apiFetch<TokenPair>("/auth/register/", {
    method: "POST",
    body: JSON.stringify(invite ? { email, password, invite } : { email, password }),
  });
  setTokens(tokens.access, tokens.refresh);
  return tokens;
}

export function forgotPassword(email: string): Promise<void> {
  return apiFetch<void>("/auth/forgot-password/", { method: "POST", body: JSON.stringify({ email }) });
}

export function resetPassword(token: string, newPassword: string): Promise<void> {
  return apiFetch<void>("/auth/reset-password/", {
    method: "POST",
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

export async function login(email: string, password: string): Promise<TokenPair> {
  const tokens = await apiFetch<TokenPair>("/auth/login/", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  setTokens(tokens.access, tokens.refresh);
  return tokens;
}

export async function logout(): Promise<void> {
  const refresh = getRefreshToken();
  if (refresh) {
    try {
      await apiFetch("/auth/logout/", { method: "POST", body: JSON.stringify({ refresh }) });
    } catch {
      // Best-effort : on nettoie les tokens locaux même si l'appel serveur échoue.
    }
  }
  clearTokens();
}

export function getMe(): Promise<Profile> {
  return apiFetch<Profile>("/auth/me/");
}

type ProfileUpdate = Partial<
  Pick<
    Profile,
    | "display_name"
    | "handle"
    | "bio"
    | "visibility"
    | "is_lead"
    | "is_follower"
    | "onboarding_completed"
    | "email_notifications_enabled"
  >
>;

export function updateMe(data: ProfileUpdate): Promise<Profile> {
  return apiFetch<Profile>("/auth/me/", { method: "PATCH", body: JSON.stringify(data) });
}

export function uploadAvatar(file: File): Promise<Profile> {
  const formData = new FormData();
  formData.append("avatar", file);
  return apiFetch<Profile>("/auth/me/", { method: "PATCH", body: formData });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiFetch<void>("/auth/change-password/", {
    method: "POST",
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

export interface HandleDisponible {
  disponible: boolean;
  detail?: string;
}

export function checkHandleDisponible(handle: string): Promise<HandleDisponible> {
  return apiFetch<HandleDisponible>(`/profiles/handle-disponible/?handle=${encodeURIComponent(handle)}`);
}

export interface SearchResult {
  handle: string;
  display_name: string;
  avatar: string | null;
  has_series: boolean;
  series_id: string | null;
  series_title: string | null;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export async function searchProfiles(q: string): Promise<SearchResult[]> {
  const page = await apiFetch<PaginatedResponse<SearchResult>>(`/search/profiles/?q=${encodeURIComponent(q)}`);
  return page.results;
}

export interface Series {
  id: string;
  title: string;
  intro: string;
  cover: string | null;
  /** Miniature générée par l'IA (Phase 5) — prioritaire sur `cover` à l'affichage. */
  cover_url: string | null;
  color: string;
  visibility: "privee" | "sur_invitation" | "publique";
  season: number;
  owner_handle: string;
  owner_display_name: string;
  /** Statut de suivi de la personne connectée pour CETTE série — `null` si
   * aucune demande. Sert à l'écran de blocage 403 (voir SeriesAccessBlocked) :
   * casting/épisodes restent inaccessibles à une simple visiteuse même sur
   * une série publique (voir apps.content.permissions.serie_accessible_ou_403
   * côté back), contrairement au détail de la série lui-même. */
  follow_status: SeriesMemberStatus | null;
  /** Id du SeriesMember correspondant — `null` si `follow_status` est `null`. */
  membership_id: string | null;
  created_at: string;
  updated_at: string;
}

type SeriesCreate = Pick<Series, "title" | "intro" | "visibility"> & Partial<Pick<Series, "color">>;
type SeriesUpdate = Partial<Pick<Series, "title" | "intro" | "visibility" | "color">>;

export function createSeries(data: SeriesCreate): Promise<Series> {
  return apiFetch<Series>("/series/", { method: "POST", body: JSON.stringify(data) });
}

export async function getMySeries(): Promise<Series[]> {
  const page = await apiFetch<PaginatedResponse<Series>>("/series/mine/");
  return page.results;
}

/** Série phare (accueil, refonte accueil) : Series enrichie des infos
 * d'épisode nécessaires à la carte carrousel — voir SeriesFeaturedSerializer
 * côté backend, qui les calcule pour éviter un appel par série. */
export interface FeaturedSeries extends Series {
  total_episodes: number;
  last_episode_at: string | null;
  last_episode_title: string | null;
  last_episode_type: string | null;
}

/** Jusqu'à 3 séries "phares" (plus grosse interaction), déjà triées par le
 * backend — voir GET /series/featured/. */
export async function getFeaturedSeries(): Promise<FeaturedSeries[]> {
  const page = await apiFetch<PaginatedResponse<FeaturedSeries>>("/series/featured/");
  return page.results;
}

/** Suggestion (accueil, section "Suggestions") : Series publique non suivie,
 * enrichie du nombre d'épisodes — voir SeriesDiscoverSerializer côté
 * backend. Pas de last_episode (la section n'en affiche pas). */
export interface DiscoverSeries extends Series {
  total_episodes: number;
}

/** Séries publiques non suivies, à découvrir — voir GET /series/discover/.
 * Suivre l'une d'elles est toujours immédiat (jamais de statut "demandé",
 * décision actée — voir SeriesDiscoverView). */
export async function getDiscoverSeries(): Promise<DiscoverSeries[]> {
  const page = await apiFetch<PaginatedResponse<DiscoverSeries>>("/series/discover/");
  return page.results;
}

/** Épisode du flux "Derniers épisodes" (accueil) : version allégée d'Episode
 * avec sa série imbriquée entière (même forme que partout ailleurs — pas
 * de champs series_* ad-hoc) et le numéro calculé côté serveur (E01, E02...,
 * cohérent avec la page épisodes de cette série) — voir
 * RecentEpisodeSerializer côté backend. */
export interface RecentEpisode {
  id: string;
  series: Series;
  episode_number: number;
  title: string;
  summary: string;
  type: string;
  happened_at: string;
  /** Miniature propre à CET épisode (choisie côté IA) — distincte de
   * series.cover_url, la cover de la série elle-même. Peut être null (pas
   * tous les épisodes en ont une), voir EpisodeRow pour le repli. */
  cover_url: string | null;
  person_name: string | null;
}

/** Épisodes les plus récents toutes séries confondues (possédées + suivies),
 * déjà triés par le backend — voir GET /episodes/recent/. Paginé comme le
 * reste de l'API (PaginatedResponse) : la page d'accueil n'affiche que les
 * tout premiers (page 1 par défaut), "Tout voir" (/app/episodes) demande
 * les pages suivantes via `page`. */
export async function getRecentEpisodes(page = 1): Promise<PaginatedResponse<RecentEpisode>> {
  return apiFetch<PaginatedResponse<RecentEpisode>>(`/episodes/recent/?page=${page}`);
}

export function updateSeries(seriesId: string, data: SeriesUpdate): Promise<Series> {
  return apiFetch<Series>(`/series/${seriesId}/`, { method: "PATCH", body: JSON.stringify(data) });
}

export function deleteSeries(seriesId: string): Promise<void> {
  return apiFetch<void>(`/series/${seriesId}/`, { method: "DELETE" });
}

export type SeriesMemberStatus = "pending" | "invited" | "accepted" | "blocked";

export interface SeriesMembership {
  id: string;
  series_id: string;
  role: "owner" | "friend";
  status: SeriesMemberStatus;
  joined_at: string;
}

export function followSeries(seriesId: string): Promise<SeriesMembership> {
  return apiFetch<SeriesMembership>(`/series/${seriesId}/follow/`, { method: "POST" });
}

export interface SeriesFollowStatus {
  follow_status: SeriesMemberStatus | null;
  membership_id: string | null;
}

/** Comme Series.follow_status/membership_id, mais lisible même quand
 * GET /series/{id}/ échoue en 403 (série privée/sur-invitation dont on n'est
 * pas amie — voir SeriesAccessBlocked) : lecture seule, ne crée jamais de
 * demande (contrairement à followSeries, un POST). */
export function getSeriesFollowStatus(seriesId: string): Promise<SeriesFollowStatus> {
  return apiFetch<SeriesFollowStatus>(`/series/${seriesId}/follow-status/`);
}

export function inviteToSeries(seriesId: string, handle: string): Promise<SeriesMembership> {
  return apiFetch<SeriesMembership>(`/series/${seriesId}/invite/`, {
    method: "POST",
    body: JSON.stringify({ handle }),
  });
}

export function inviteToSeriesByEmail(seriesId: string, email: string): Promise<unknown> {
  return apiFetch<unknown>(`/series/${seriesId}/invite/`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function getSeries(seriesId: string): Promise<Series> {
  return apiFetch<Series>(`/series/${seriesId}/`);
}

export interface Media {
  id: string;
  series: string;
  entity_type: "person" | "episode";
  entity_id: string;
  type: "photo" | "capture" | "video" | "audio";
  file: string;
  caption: string;
  visibility: "admin_only" | "all";
  /** True si ce média est actuellement l'avatar du personnage (person uniquement). */
  is_avatar: boolean;
  created_at: string;
}

export interface ReactionSummary {
  emoji: string;
  count: number;
  mine: boolean;
}

export interface Interactions {
  comments: Comment[];
  like_count: number;
  liked_by_me: boolean;
  reactions: ReactionSummary[];
}

export interface Person {
  id: string;
  series: string;
  name: string;
  age: number | null;
  job: string;
  person_role: "moi" | "pretendant" | "amie" | "famille" | "collegue" | "voisin" | "nemesis" | "figurant";
  status: "en_cours" | "termine" | "chouchou" | "ghosting" | "en_froid" | "boulet" | "love" | "no_news";
  interest_level: number;
  /** Utilisés pour adapter les prompts d'image générés par l'IA (voir apps.ai côté back). */
  nature: "human" | "animal" | "other";
  nature_description: string;
  presentation: "feminine" | "masculine" | "other";
  bio: string;
  queen_notes: string;
  green_flags: string[];
  red_flags: string[];
  tags: string[];
  /**
   * Avatar résolu à afficher : photo de galerie choisie, sinon upload direct,
   * sinon null (→ initiales côté front). `avatar` (upload direct brut) est en
   * écriture seule côté API — voir uploadPersonAvatar.
   */
  avatar_url: string | null;
  is_favorite: boolean;
  is_archived: boolean;
  is_lead_character: boolean;
  media: Media[];
  interactions: Interactions;
  created_at: string;
  updated_at: string;
}

type PersonWrite = Partial<
  Pick<
    Person,
    | "name"
    | "age"
    | "job"
    | "person_role"
    | "status"
    | "interest_level"
    | "nature"
    | "nature_description"
    | "presentation"
    | "bio"
    | "queen_notes"
    | "green_flags"
    | "red_flags"
    | "tags"
    | "is_archived"
  >
>;

export interface PersonFilters {
  person_role?: string;
  tag?: string;
  search?: string;
}

export async function getPeople(seriesId: string, filters: PersonFilters = {}): Promise<Person[]> {
  const params = new URLSearchParams();
  if (filters.person_role) params.set("person_role", filters.person_role);
  if (filters.tag) params.set("tag", filters.tag);
  if (filters.search) params.set("search", filters.search);
  const query = params.toString() ? `?${params.toString()}` : "";
  const page = await apiFetch<PaginatedResponse<Person>>(`/series/${seriesId}/people/${query}`);
  return page.results;
}

export function createPerson(seriesId: string, data: PersonWrite): Promise<Person> {
  return apiFetch<Person>(`/series/${seriesId}/people/`, { method: "POST", body: JSON.stringify(data) });
}

export function getPerson(personId: string): Promise<Person> {
  return apiFetch<Person>(`/people/${personId}/`);
}

export function updatePerson(personId: string, data: PersonWrite): Promise<Person> {
  return apiFetch<Person>(`/people/${personId}/`, { method: "PATCH", body: JSON.stringify(data) });
}

export function deletePerson(personId: string): Promise<void> {
  return apiFetch<void>(`/people/${personId}/`, { method: "DELETE" });
}

export function togglePersonFavorite(personId: string): Promise<Person> {
  return apiFetch<Person>(`/people/${personId}/favorite/`, { method: "POST" });
}

export function uploadPersonAvatar(personId: string, file: File): Promise<Person> {
  const formData = new FormData();
  formData.append("avatar", file);
  return apiFetch<Person>(`/people/${personId}/`, { method: "PATCH", body: formData });
}

/** Roman-photo (remplacé par le format épisode animé, voir plus bas) — UI
 * frontend supprimée (PhotoStorySection/PhotoStoryCarousel, voir historique
 * Git), mais ce type et le champ `Episode.photo_story` restent : le backend
 * renvoie toujours ce champ pour les anciens épisodes déjà générés ainsi. */
export interface PhotoStorySlide {
  index: number;
  caption: string;
  image_url: string;
  source_media_id: string | null;
}

export interface PhotoStory {
  status: "pending" | "generating" | "ready" | "failed" | null;
  captions?: {
    headlines: string[];
    caption: string;
    slides: string[];
  };
  slides?: PhotoStorySlide[];
  /** Détail de l'échec (status "failed" uniquement) — ex. quota d'images épuisé. */
  error?: string;
  generated_at?: string;
}

export interface Episode {
  id: string;
  series: string;
  person: string | null;
  title: string;
  summary: string;
  story: string;
  details: string;
  type: string;
  location: string;
  happened_at: string;
  queen_mood: number | null;
  green_flags: string[];
  red_flags: string[];
  is_private: boolean;
  /** Brouillon (false) / publié (true) — bloc Publication. Invisible de
   * toute abonnée tant que false, quel que soit is_private/l'audience. */
  is_published: boolean;
  /** Posé par le serveur à la publication, jamais par ce client — lecture
   * seule (voir apps.content.serializers.EpisodeSerializer). */
  notified_at: string | null;
  photo_story: PhotoStory | null;
  media: Media[];
  interactions: Interactions;
  /** Miniature choisie via le flux de création vocale IA (Phase 5) — null sinon. */
  cover_url: string | null;
  created_at: string;
  updated_at: string;
}

type EpisodeWrite = Partial<
  Pick<
    Episode,
    | "person"
    | "title"
    | "summary"
    | "story"
    | "details"
    | "type"
    | "location"
    | "happened_at"
    | "queen_mood"
    | "green_flags"
    | "red_flags"
    | "is_private"
    | "is_published"
  >
>;

export async function getEpisodes(seriesId: string): Promise<Episode[]> {
  const page = await apiFetch<PaginatedResponse<Episode>>(`/series/${seriesId}/episodes/`);
  return page.results;
}

export function createEpisode(seriesId: string, data: EpisodeWrite): Promise<Episode> {
  return apiFetch<Episode>(`/series/${seriesId}/episodes/`, { method: "POST", body: JSON.stringify(data) });
}

export function getEpisode(episodeId: string): Promise<Episode> {
  return apiFetch<Episode>(`/episodes/${episodeId}/`);
}

export function updateEpisode(episodeId: string, data: EpisodeWrite): Promise<Episode> {
  return apiFetch<Episode>(`/episodes/${episodeId}/`, { method: "PATCH", body: JSON.stringify(data) });
}

export function deleteEpisode(episodeId: string): Promise<void> {
  return apiFetch<void>(`/episodes/${episodeId}/`, { method: "DELETE" });
}

interface MediaUpload {
  series: string;
  entity_type: "person" | "episode" | "series";
  entity_id: string;
  type: Media["type"];
  file: File;
  caption?: string;
  visibility?: Media["visibility"];
}

export function uploadMedia(data: MediaUpload): Promise<Media> {
  const formData = new FormData();
  formData.append("series", data.series);
  formData.append("entity_type", data.entity_type);
  formData.append("entity_id", data.entity_id);
  formData.append("type", data.type);
  formData.append("file", data.file);
  if (data.caption) formData.append("caption", data.caption);
  formData.append("visibility", data.visibility ?? "admin_only");
  return apiFetch<Media>("/media/", { method: "POST", body: formData });
}

export function setMediaAsAvatar(mediaId: string): Promise<Person> {
  return apiFetch<Person>(`/media/${mediaId}/set-as-avatar/`, { method: "POST" });
}

/** Désigne une photo déjà uploadée comme miniature d'un épisode ou d'une série —
 * alternative à une miniature générée par l'IA. `T` selon le type de l'entité
 * ciblée (Episode ou Series), déduit par l'appelant. */
export function setMediaAsCover<T extends Episode | Series>(mediaId: string): Promise<T> {
  return apiFetch<T>(`/media/${mediaId}/set-as-cover/`, { method: "POST" });
}

export function deleteMedia(mediaId: string): Promise<void> {
  return apiFetch<void>(`/media/${mediaId}/`, { method: "DELETE" });
}

// --- Commentaires, likes, réactions (Phase 4) ------------------------------

export interface Comment {
  id: string;
  target_type: "person" | "episode";
  target_id: string;
  text: string;
  /** Lien KLIPY (chantier GIFs) — jamais ré-hébergé chez nous. Texte et GIF
   * sont chacun facultatifs individuellement, mais pas les deux à la fois
   * (voir CommentSerializer.validate côté backend). */
  gif_url: string | null;
  /** Réponses sur un seul niveau (comme Instagram) : null pour un commentaire
   * racine, sinon l'id de la racine (jamais d'une autre réponse — remonté
   * côté serveur, voir CommentCreateView.perform_create). */
  parent: string | null;
  author_handle: string;
  author_display_name: string;
  author_avatar: string | null;
  is_owner_comment: boolean;
  can_delete: boolean;
  /** Une seule réaction par personne (voir COMMENT_REACTION_EMOJIS) mais
   * plusieurs emojis différents peuvent coexister, un par personne — même
   * forme de regroupement que ReactionSummary (épisode/personnage). */
  reactions: ReactionSummary[];
  created_at: string;
}

export function createComment(
  targetType: "person" | "episode",
  targetId: string,
  text: string,
  parent?: string,
  gifUrl?: string | null
): Promise<Comment> {
  return apiFetch<Comment>("/comments/", {
    method: "POST",
    body: JSON.stringify({ target_type: targetType, target_id: targetId, text, parent, gif_url: gifUrl ?? null }),
  });
}

export function deleteComment(commentId: string): Promise<void> {
  return apiFetch<void>(`/comments/${commentId}/`, { method: "DELETE" });
}

/** Résultat allégé renvoyé par notre proxy (voir apps.social.services.klipy
 * côté backend) — jamais les 20 URLs brutes de KLIPY, juste ce qui sert à
 * l'affichage (preview_url, vignette statique) et à l'envoi (gif_url, celui
 * réellement posté dans le commentaire). Le navigateur n'appelle jamais
 * KLIPY directement, uniquement GET /gifs/search/ (ce proxy). */
export interface GifResult {
  id: number;
  title: string;
  gif_url: string | null;
  preview_url: string | null;
  /** Même vignette que preview_url (xs), mais animée — aperçu au survol
   * souris / appui tactile maintenu (voir GifPicker), jamais affichée pour
   * toute la grille à la fois. */
  preview_gif_url: string | null;
  width: number | null;
  height: number | null;
}

export interface GifSearchResult {
  results: GifResult[];
  page: number;
  has_next: boolean;
}

export function searchGifs(q: string, page = 1, perPage = 24): Promise<GifSearchResult> {
  const params = new URLSearchParams({ q, page: String(page), per_page: String(perPage) });
  return apiFetch<GifSearchResult>(`/gifs/search/?${params.toString()}`);
}

export function toggleLike(targetType: "person" | "episode", targetId: string): Promise<{ liked: boolean }> {
  return apiFetch<{ liked: boolean }>("/likes/toggle/", {
    method: "POST",
    body: JSON.stringify({ target_type: targetType, target_id: targetId }),
  });
}

export const REACTION_EMOJIS = ["😍", "😬", "💀", "👀"] as const;

export function toggleReaction(
  targetType: "person" | "episode",
  targetId: string,
  emoji: string
): Promise<{ active: boolean }> {
  return apiFetch<{ active: boolean }>("/reactions/toggle/", {
    method: "POST",
    body: JSON.stringify({ target_type: targetType, target_id: targetId, emoji }),
  });
}

/** Set fixe façon WhatsApp — distinct de REACTION_EMOJIS (personnage/épisode,
 * multi-emoji). Sur un commentaire : une seule réaction par personne, en
 * poser une nouvelle remplace la précédente (voir ReactionToggleView,
 * backend). */
export const COMMENT_REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"] as const;

export function toggleCommentReaction(commentId: string, emoji: string): Promise<{ active: boolean }> {
  return apiFetch<{ active: boolean }>("/reactions/toggle/", {
    method: "POST",
    body: JSON.stringify({ target_type: "comment", target_id: commentId, emoji }),
  });
}

// --- Paris (Phase 4, mécanique de points maison) ---------------------------

export interface BetOption {
  id: string;
  label: string;
  vote_count: number;
  /** Cote en direct (total_votes / vote_count) : null si personne n'a encore voté cette option. */
  cote: number | null;
}

export interface Bet {
  id: string;
  series: string;
  created_by_handle: string;
  person: string | null;
  person_name: string | null;
  title: string;
  description: string;
  status: "draft" | "open" | "closed" | "resolved" | "rejected";
  closes_at: string | null;
  options: BetOption[];
  winning_option_id: string | null;
  /** Option choisie par l'utilisateur connecté, si elle a déjà voté. */
  my_vote: string | null;
  total_votes: number;
  /** Mise fixe par vote (constante backend, voir Bet.MISE_FIXE côté Django). */
  mise_fixe: number;
  created_at: string;
  updated_at: string;
}

export interface BetCreate {
  title: string;
  description?: string;
  person?: string | null;
  closes_at?: string | null;
  option_labels: string[];
}

export async function getBets(seriesId: string): Promise<Bet[]> {
  const page = await apiFetch<PaginatedResponse<Bet>>(`/series/${seriesId}/bets/`);
  return page.results;
}

export function createBet(seriesId: string, data: BetCreate): Promise<Bet> {
  return apiFetch<Bet>(`/series/${seriesId}/bets/`, { method: "POST", body: JSON.stringify(data) });
}

export function updateBetStatus(
  betId: string,
  data: { status: Bet["status"]; winning_option_id?: string | null }
): Promise<Bet> {
  return apiFetch<Bet>(`/bets/${betId}/`, { method: "PATCH", body: JSON.stringify(data) });
}

export function voteBet(betId: string, optionId: string): Promise<Bet> {
  return apiFetch<Bet>(`/bets/${betId}/vote/`, { method: "POST", body: JSON.stringify({ option_id: optionId }) });
}

export interface LeaderboardEntry {
  handle: string;
  display_name: string;
  avatar_url: string | null;
  score: number;
}

export function getLeaderboard(seriesId: string): Promise<LeaderboardEntry[]> {
  return apiFetch<LeaderboardEntry[]>(`/series/${seriesId}/leaderboard/`);
}

// --- Notifications -----------------------------------------------------

export interface Notification {
  id: string;
  series_id: string | null;
  /** Nulle si la notif n'a pas de série (rare) — voir NOTIFICATION_TYPES_AVEC_ETIQUETTE_SERIE. */
  series_title: string | null;
  type:
    | "new_episode"
    | "new_person"
    | "new_bet_open"
    | "friend_proposed_bet"
    | "follow_request"
    | "follow_accepted"
    | "direct_follow"
    | "series_invite"
    | "bet_resolved";
  text: string;
  is_read: boolean;
  link: string;
  created_at: string;
}

export async function getNotifications(): Promise<Notification[]> {
  const page = await apiFetch<PaginatedResponse<Notification>>("/notifications/");
  return page.results;
}

export function markAllNotificationsRead(): Promise<void> {
  return apiFetch<void>("/notifications/read-all/", { method: "POST" });
}

/** Marque UNE notification comme lue (contrairement à markAllNotificationsRead,
 * qui les marque toutes) — voir notifications-context.tsx::marquerLu,
 * déclenché par le clic sur une notif précise dans NotificationBell. */
export function markNotificationRead(notificationId: string): Promise<void> {
  return apiFetch<void>(`/notifications/${notificationId}/read/`, { method: "POST" });
}

// --- Demandes de suivi (Phase 4) -------------------------------------------

export interface FollowRequestEntry {
  id: string;
  handle: string;
  display_name: string;
  avatar_url: string | null;
  status: "pending";
  joined_at: string;
}

export async function getSeriesRequests(seriesId: string): Promise<FollowRequestEntry[]> {
  const page = await apiFetch<PaginatedResponse<FollowRequestEntry>>(`/series/${seriesId}/requests/`);
  return page.results;
}

export function acceptFollowRequest(requestId: string): Promise<FollowRequestEntry> {
  return apiFetch<FollowRequestEntry>(`/follow-requests/${requestId}/accept/`, { method: "POST" });
}

export function declineFollowRequest(requestId: string): Promise<void> {
  return apiFetch<void>(`/follow-requests/${requestId}/decline/`, { method: "POST" });
}

export function unfollowSeries(seriesId: string): Promise<void> {
  return apiFetch<void>(`/series/${seriesId}/follow/`, { method: "DELETE" });
}

export interface FollowRequestDetail {
  id: string;
  series_id: string;
  series_title: string;
  owner_handle: string;
  owner_display_name: string;
  handle: string;
  display_name: string;
  status: SeriesMemberStatus;
  joined_at: string;
}

/** Détail d'une demande/invitation (page d'acceptation d'invitation). */
export function getFollowRequest(requestId: string): Promise<FollowRequestDetail> {
  return apiFetch<FollowRequestDetail>(`/follow-requests/${requestId}/`);
}

// --- Accès sélectif aux épisodes : groupes de diffusion + abonnées d'une série ---

/** Suit `next` uniquement comme condition d'arrêt (pas comme URL à requêter —
 * apiFetch préfixe systématiquement API_BASE_URL, un `next` absolu casserait
 * l'appel) : avance via `?page=N` à la main, dans l'esprit de getRecentEpisodes.
 * Nécessaire ici : GET /members/ et GET /diffusion-groups/ alimentent des
 * checklists qui ont besoin de la liste COMPLÈTE, pas juste la 1ère page
 * (contrairement à getPeople/getSeriesRequests, qui restent volontairement
 * sur la 1ère page — listes toujours courtes dans leur usage actuel). */
async function fetchAllPages<T>(path: string): Promise<T[]> {
  const tous: T[] = [];
  let page = 1;
  for (;;) {
    const separateur = path.includes("?") ? "&" : "?";
    const reponse = await apiFetch<PaginatedResponse<T>>(`${path}${separateur}page=${page}`);
    tous.push(...reponse.results);
    if (!reponse.next) break;
    page += 1;
  }
  return tous;
}

export interface SeriesMemberEntry {
  id: string;
  handle: string;
  display_name: string;
  avatar_url: string | null;
  status: string;
  joined_at: string;
}

/** Abonnées ACCEPTÉES d'une série (GET /series/{id}/members/), toutes pages
 * confondues — pour peupler une checklist (composition de groupe, plus tard
 * audience d'épisode). `id` est le member_id attendu par les endpoints
 * diffusion-groups/episode-audience. */
export function getAllSeriesMembers(seriesId: string): Promise<SeriesMemberEntry[]> {
  return fetchAllPages<SeriesMemberEntry>(`/series/${seriesId}/members/`);
}

export interface DiffusionGroup {
  id: string;
  series: string;
  name: string;
  member_ids: string[];
  created_at: string;
  updated_at: string;
}

export function getDiffusionGroups(seriesId: string): Promise<DiffusionGroup[]> {
  return fetchAllPages<DiffusionGroup>(`/series/${seriesId}/diffusion-groups/`);
}

export function createDiffusionGroup(seriesId: string, name: string): Promise<DiffusionGroup> {
  return apiFetch<DiffusionGroup>(`/series/${seriesId}/diffusion-groups/`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export function renameDiffusionGroup(groupId: string, name: string): Promise<DiffusionGroup> {
  return apiFetch<DiffusionGroup>(`/diffusion-groups/${groupId}/`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export function deleteDiffusionGroup(groupId: string): Promise<void> {
  return apiFetch<void>(`/diffusion-groups/${groupId}/`, { method: "DELETE" });
}

/** Remplacement intégral de la composition du groupe. */
export function setDiffusionGroupMembers(groupId: string, memberIds: string[]): Promise<DiffusionGroup> {
  return apiFetch<DiffusionGroup>(`/diffusion-groups/${groupId}/members/`, {
    method: "PUT",
    body: JSON.stringify({ member_ids: memberIds }),
  });
}

export interface EpisodeAudience {
  member_ids: string[];
}

export function getEpisodeAudience(episodeId: string): Promise<EpisodeAudience> {
  return apiFetch<EpisodeAudience>(`/episodes/${episodeId}/audience/`);
}

/** Remplacement intégral de l'audience d'un épisode. Prend toujours la liste
 * finale de member_ids déjà résolue côté client (jamais group_id ici) : le
 * groupe ne sert qu'à pré-cocher localement (voir EpisodeAudienceModal) — si
 * l'utilisatrice décoche quelqu'un après pré-cochage, envoyer group_id
 * écraserait son ajustement en ré-étendant le groupe côté serveur. */
export function setEpisodeAudience(episodeId: string, memberIds: string[]): Promise<EpisodeAudience> {
  return apiFetch<EpisodeAudience>(`/episodes/${episodeId}/audience/`, {
    method: "PUT",
    body: JSON.stringify({ member_ids: memberIds }),
  });
}

// --- Profil public (Phase 4) ------------------------------------------------

export interface PublicSeries {
  id: string;
  title: string;
  intro: string;
  cover: string | null;
  color: string;
  visibility: Series["visibility"];
  /** null = pas encore suivie ; sinon le statut du SeriesMember. */
  follow_status: SeriesMemberStatus | null;
  /** Id du SeriesMember correspondant, utile pour /app/invitations/{id} — null si follow_status est null. */
  membership_id: string | null;
}

export interface PublicProfile {
  handle: string;
  display_name: string;
  avatar: string | null;
  bio: string;
  series: PublicSeries[];
}

export function getPublicProfile(handle: string): Promise<PublicProfile> {
  return apiFetch<PublicProfile>(`/profiles/${encodeURIComponent(handle)}/`);
}

// --- Génération IA (Phase 5) -------------------------------------------

export interface VoiceAnalysis {
  title: string;
  summary: string;
  type: string;
  location: string;
  queen_mood: number | null;
  green_flags: string[];
  red_flags: string[];
  points_delta: number;
  points_reason: string;
}

interface AnalyzeVoiceParams {
  audioFile?: File | null;
  transcript?: string;
}

/** Transcription (faster-whisper, si `audioFile` fourni) puis analyse (Claude) —
 * un seul appel réseau, la complexité à deux étages reste côté serveur. */
export function analyzeVoice(seriesId: string, personId: string, params: AnalyzeVoiceParams): Promise<VoiceAnalysis> {
  const formData = new FormData();
  formData.append("series", seriesId);
  formData.append("person", personId);
  if (params.audioFile) formData.append("audio", params.audioFile);
  if (params.transcript) formData.append("transcript", params.transcript);
  return apiFetch<VoiceAnalysis>("/episodes/analyze-voice/", { method: "POST", body: formData });
}

/** Transcrit un seul segment de dictée (≤1min20) — voir useSegmentedAudioRecorder :
 * la dictée est découpée en segments côté front pour rester sous le temps de
 * transcription supporté par le serveur (~1,27x le temps réel, mesuré). */
export async function transcribeChunk(seriesId: string, audioFile: File): Promise<string> {
  const formData = new FormData();
  formData.append("series", seriesId);
  formData.append("audio", audioFile);
  const resultat = await apiFetch<{ transcript: string }>("/episodes/transcribe-chunk/", {
    method: "POST",
    body: formData,
  });
  return resultat.transcript;
}

/** Écran "Ré-écouter / Améliorer avec l'IA / Valider", juste après l'enregistrement
 * (voir VoiceReviewAudioStep) — la prise vocale complète (segments concaténés
 * côté serveur, voir apps.ai.services.audio), avant même que l'épisode existe. */
export interface VoiceTake {
  id: string;
  cleanup_status: "none" | "processing" | "ready" | "failed";
  cleanup_error: string;
  original_url: string;
  cleaned_url: string | null;
  created_at: string;
}

/** Assemble les segments enregistrés (voir useSegmentedAudioRecorder) en une
 * seule prise MP3, côté serveur — appelé dès la fin de l'enregistrement. */
export function createVoiceTake(seriesId: string, segments: File[]): Promise<VoiceTake> {
  const formData = new FormData();
  formData.append("series", seriesId);
  segments.forEach((segment) => formData.append("segments", segment));
  return apiFetch<VoiceTake>("/voice-takes/", { method: "POST", body: formData });
}

/** Statut de la prise (polling pendant "Améliorer avec l'IA" — voir VoiceReviewAudioStep). */
export function getVoiceTake(voiceTakeId: string): Promise<VoiceTake> {
  return apiFetch<VoiceTake>(`/voice-takes/${voiceTakeId}/`);
}

/** "Améliorer avec l'IA" (Cleanvoice) — jamais automatique, ça coûte de l'argent et
 * du quota : uniquement sur ce bouton explicite. Asynchrone (voir getVoiceTake). */
export function cleanupVoiceTake(voiceTakeId: string): Promise<VoiceTake> {
  return apiFetch<VoiceTake>(`/voice-takes/${voiceTakeId}/cleanup/`, { method: "POST" });
}

/** "Valider" — fixe l'audio final de l'épisode (la prise complète, brute ou
 * nettoyée selon le choix) comme unique Media audio, voir Episode.active_audio. */
export function attachVoiceTake(episodeId: string, voiceTakeId: string, variant: "original" | "cleaned"): Promise<Episode> {
  return apiFetch<Episode>(`/episodes/${episodeId}/attach-voice-take/`, {
    method: "POST",
    body: JSON.stringify({ voice_take_id: voiceTakeId, variant }),
  });
}

export interface ThumbnailsBatch {
  batch_id: string;
  thumbnails: string[];
}

interface GenerateThumbnailsOptions {
  title?: string;
  queenMood?: number | null;
}

export function generateThumbnails(
  seriesId: string,
  personId: string | null,
  summary: string,
  type: string,
  options: GenerateThumbnailsOptions = {}
): Promise<ThumbnailsBatch> {
  return apiFetch<ThumbnailsBatch>("/episodes/generate-thumbnails/", {
    method: "POST",
    body: JSON.stringify({
      series: seriesId,
      person: personId,
      summary,
      type,
      title: options.title ?? "",
      queen_mood: options.queenMood ?? null,
    }),
  });
}

export function attachThumbnail(episodeId: string, batchId: string, index: number): Promise<Episode> {
  return apiFetch<Episode>(`/episodes/${episodeId}/attach-thumbnail/`, {
    method: "POST",
    body: JSON.stringify({ batch_id: batchId, index }),
  });
}

/** Génère 4 miniatures pour la couverture d'une série (titre + intro déjà en base, rien à envoyer). */
export function generateSeriesCover(seriesId: string): Promise<ThumbnailsBatch> {
  return apiFetch<ThumbnailsBatch>(`/series/${seriesId}/generate-cover/`, { method: "POST" });
}

export function attachSeriesCover(seriesId: string, batchId: string, index: number): Promise<Series> {
  return apiFetch<Series>(`/series/${seriesId}/attach-cover/`, {
    method: "POST",
    body: JSON.stringify({ batch_id: batchId, index }),
  });
}

/** Format épisode animé (voir docs/format-episode-anime.md) — remplace le roman-photo ci-dessus. */
export interface TimelineSubtitle {
  start: number;
  end: number;
  text: string;
}

export interface TimelineCharacter {
  character_id: string;
  sticker_url: string | null;
  // Identité (voir apps.ai.services.timeline.identite_personnage côté back) —
  // sert au repli emoji + nom quand sticker_url est nul (voir EpisodePlayer,
  // emojiRepliPersonnage dans content-labels.ts). Optionnels : absents des
  // épisodes générés avant cet ajout, jamais backfillés en base.
  name?: string;
  nature?: "human" | "animal" | "other";
  nature_description?: string;
  presentation?: "feminine" | "masculine" | "other";
  mood: "neutral" | "shocked" | "happy" | "angry";
  anchor: string;
  scale: number;
  flip: boolean;
  animation_in: string;
  animation_idle: string;
  animation_out: string;
}

/** Lot 5 : emoji/onomatopée/bulle (texte libre borné côté serveur) ou image
 * perso (import, voir OverlayAsset) — jamais produits par Claude, ajoutés
 * seulement depuis l'éditeur. */
export interface TimelineOverlay {
  type: "emoji" | "onomatopoeia" | "bubble" | "image";
  /** Texte (emoji/onomatopée/bulle) ou URL résolue de l'image (type "image"). */
  value: string;
  /** Uniquement pour type "image" — id de l'OverlayAsset importé, à renvoyer
   * tel quel dans un futur PATCH (voir patchEpisodeTimeline) : sans lui, le
   * serveur ne peut plus résoudre l'image et l'overlay disparaîtrait. */
  asset_id?: string;
  anchor: string;
  scale: number;
  animation_in: string;
  animation_idle: string;
  animation_out: string;
}

export interface TimelineSlide {
  index: number;
  start: number;
  end: number;
  background: {
    type: "image";
    source: "ai" | "upload";
    url: string | null;
    prompt: string;
    animation: string;
    /** Absent du JSON de la doc (§2), ajout additif Lot 4 : id du Decor
     * sélectionné, pour le picker de bibliothèque et la régénération. */
    decor_id?: string | null;
  };
  characters: TimelineCharacter[];
  overlays: TimelineOverlay[];
  /** Présent uniquement en mode carrousel (pas d'audio, voir EpisodeTimelineData.audio.url)
   * — sous-titre de cette slide, affiché directement sans recherche par timecode. */
  caption?: string;
}

export interface EpisodeTimelineData {
  episode_id: string;
  serie_id: string;
  version: number;
  /** `url: null` = mode carrousel (épisode sans audio) : navigation manuelle
   * entre les slides côté lecteur, `duration` n'est alors qu'une valeur
   * nominale (nombre de slides), jamais un vrai chronométrage. */
  audio: { url: string | null; duration: number };
  subtitles: TimelineSubtitle[];
  slides: TimelineSlide[];
}

export interface EpisodeTimelineStatus {
  status: "pending" | "generating" | "ready" | "failed" | null;
  data?: EpisodeTimelineData | null;
  error?: string;
  /** Pendant la génération uniquement (voir apps.ai.tasks._progression). */
  progression?: { pourcentage: number; etape: string } | null;
}

export function getEpisodeTimeline(episodeId: string): Promise<EpisodeTimelineStatus> {
  return apiFetch<EpisodeTimelineStatus>(`/episodes/${episodeId}/generate-timeline/`);
}

/** Ne prend plus de fichier (voir historique Git) : réutilise ce que l'épisode
 * contient déjà côté serveur (audio existant, sinon description écrite) —
 * jamais de sélecteur de fichier déclenché par ce bouton. */
export function generateEpisodeTimeline(episodeId: string): Promise<EpisodeTimelineStatus> {
  return apiFetch<EpisodeTimelineStatus>(`/episodes/${episodeId}/generate-timeline/`, { method: "POST" });
}

/** Lot 4 (éditeur) — édition partielle : { index, text } par sous-titre modifié,
 * { index, characters?, overlays? } par slide modifiée. Revalidé strictement
 * côté serveur contre le vocabulaire fermé (voir TimelinePatchView). */
export interface TimelinePatch {
  subtitles?: { index: number; text: string }[];
  slides?: {
    index: number;
    characters?: Partial<TimelineCharacter>[];
    overlays?: Partial<TimelineOverlay>[];
  }[];
}

export function patchEpisodeTimeline(episodeId: string, patch: TimelinePatch): Promise<EpisodeTimelineStatus> {
  return apiFetch<EpisodeTimelineStatus>(`/episodes/${episodeId}/generate-timeline/`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function setSlideDecor(episodeId: string, index: number, decorId: string): Promise<EpisodeTimelineStatus> {
  return apiFetch<EpisodeTimelineStatus>(`/episodes/${episodeId}/timeline/slides/${index}/decor/`, {
    method: "POST",
    body: JSON.stringify({ decor_id: decorId }),
  });
}

export function regenerateSlideDecor(episodeId: string, index: number): Promise<EpisodeTimelineStatus> {
  return apiFetch<EpisodeTimelineStatus>(`/episodes/${episodeId}/timeline/slides/${index}/regenerate-decor/`, {
    method: "POST",
  });
}

export interface Decor {
  id: string;
  name: string;
  category: "interieur" | "lieu_public" | "transport" | "exterieur";
  keywords: string[];
  image: string;
  is_public: boolean;
  series: string | null;
}

export async function listDecors(params: { series?: string; category?: string; q?: string } = {}): Promise<Decor[]> {
  const query = new URLSearchParams();
  if (params.series) query.set("series", params.series);
  if (params.category) query.set("category", params.category);
  if (params.q) query.set("q", params.q);
  const suffixe = query.toString() ? `?${query.toString()}` : "";
  const page = await apiFetch<PaginatedResponse<Decor>>(`/decors/${suffixe}`);
  return page.results;
}

export interface CharacterSticker {
  id: string;
  character: string;
  mood: "neutral" | "shocked" | "happy" | "angry";
  image: string;
  source: "upload" | "ai";
  is_default: boolean;
  created_at: string;
}

export async function getCharacterStickers(personId: string): Promise<CharacterSticker[]> {
  const page = await apiFetch<PaginatedResponse<CharacterSticker>>(`/people/${personId}/stickers/`);
  return page.results;
}

/** Sticker de base : Claude rédige le corps dessiné à partir de `description`
 * (+ personnage, + épisode si fourni), l'IA génère le sticker avec la photo en
 * référence. Remplace le sticker par défaut existant (200), sinon le crée (201).
 * Synchrone, ~15-25 s. */
export function generateBaseSticker(
  personId: string,
  photo: File,
  description: string,
  episodeId?: string
): Promise<CharacterSticker> {
  const formData = new FormData();
  formData.append("photo", photo);
  formData.append("description", description);
  if (episodeId) formData.append("episode", episodeId);
  return apiFetch<CharacterSticker>(`/people/${personId}/stickers/base/`, { method: "POST", body: formData });
}

/** Sticker émotion généré par IA depuis le sticker de base (renvoie
 * l'existant si cette humeur a déjà un sticker). Synchrone, ~15-25 s. */
export function generateEmotionSticker(
  personId: string,
  mood: "shocked" | "happy" | "angry"
): Promise<CharacterSticker> {
  return apiFetch<CharacterSticker>(`/people/${personId}/stickers/emotion/`, {
    method: "POST",
    body: JSON.stringify({ mood }),
  });
}

/** Supprime un sticker — si c'était le sticker par défaut, le serveur réattribue
 * automatiquement is_default au plus ancien restant (voir
 * apps.content.views.CharacterStickerDetailView.delete). N'affecte jamais les
 * épisodes déjà générés (URL du sticker figée dans leur JSON). */
export function deleteCharacterSticker(stickerId: string): Promise<void> {
  return apiFetch<void>(`/stickers/${stickerId}/`, { method: "DELETE" });
}

export interface OverlayAsset {
  id: string;
  series: string;
  image: string;
  created_at: string;
}

export async function listOverlayAssets(seriesId: string): Promise<OverlayAsset[]> {
  const page = await apiFetch<PaginatedResponse<OverlayAsset>>(`/series/${seriesId}/overlay-assets/`);
  return page.results;
}

export function uploadOverlayAsset(seriesId: string, image: File): Promise<OverlayAsset> {
  const formData = new FormData();
  formData.append("image", image);
  return apiFetch<OverlayAsset>(`/series/${seriesId}/overlay-assets/`, { method: "POST", body: formData });
}

export interface QuotaState {
  used_today: number;
  limit: number;
  is_unlimited: boolean;
}

export function getQuota(): Promise<QuotaState> {
  return apiFetch<QuotaState>("/me/quota/");
}
